
import type { PhotoAPIInterface } from './api-switcher'
import { client } from './client'
import { responseDataConverter, apiPhotoToMyPhoto, apiTrashItemsToTrashPage } from './converters'
import { uploadPhotoTwoPhase } from './two-phase-upload'

export const PhotoAPI: PhotoAPIInterface = {
  getPhoto: (photo_id, signal) => responseDataConverter(
    client.GET('/my/{PhotoId}', {
      params: {
        path: { PhotoId: photo_id },
      },
      signal
    }),
    apiPhotoToMyPhoto
  ),

  tagsToMyPhoto: (photo_id, in_user_ids) => responseDataConverter(
    client.POST('/my/{PhotoId}/tag', {
      params: {
        path: { PhotoId: photo_id },
      },
      body: { in_user_ids },
    }),
    apiPhotoToMyPhoto
  ),

  // FEATURE_TWO_PHASE_UPLOAD switches to the reserve → direct-to-S3 → poll
  // flow (design doc 2026-07-06); the V1 multipart path stays the default
  // until the toggle rolls out. width/height are only needed by V1 — the
  // two-phase backend decodes real dimensions server-side.
  uploadPhoto: (
    file, width, height, last_modified, utc_offset_minutes, signal
  ) => FEATURE_TWO_PHASE_UPLOAD ? uploadPhotoTwoPhase(
    file, last_modified, utc_offset_minutes, signal
  ) : responseDataConverter(
    client.POST('/photo', {
      body: {
        // @ts-expect-error string type specified but File is required
        file,
        last_modified_at: last_modified,
        width,
        height,
        utc_offset_minutes,
      },
      bodySerializer: (body) => {
        const formData = new FormData()
        formData.append('file', body!.file)
        formData.append('last_modified_at', body!.last_modified_at.toString())
        if (body!.utc_offset_minutes !== undefined) {
          formData.append('utc_offset_minutes', body!.utc_offset_minutes.toString())
        }
        formData.append('width', body!.width.toString())
        formData.append('height', body!.height.toString())
        return formData
      },
      signal
    }),
    () => undefined
  ),

  movePhotosToTrash: (photo_ids) => responseDataConverter(
    client.POST('/trash/add', { body: { photo_ids } }),
    () => undefined
  ),

  listAllPhotosDate: (desc, signal) => responseDataConverter(
    client.GET('/my/index', {
      params: {
        query: {
          order: desc ? 'desc' : 'asc'
        }
      },
      signal
    }),
    p => p.photo_ids!.map(added => new Date(added))
  ),

  // startTimestamp: UTC "YYYY-MM-DDTHH:mm:ss.sss"
  listPhotos: (direction, startTimestamp, signal) => responseDataConverter(
    client.GET('/my', {
      params: {
        query: {
          direction: direction ? 'past' : 'future',
          startTimestamp: startTimestamp ? startTimestamp.toISOString() : undefined
        }
      },
      signal
    }),
    p => p.photos.map(apiPhotoToMyPhoto)
  ),

  deletePhotos: (photo_ids) => responseDataConverter(
    client.POST('/trash/delete', { body: { photo_ids } }),
    () => undefined
  ),

  deleteAllPhotosFromTrash: () => responseDataConverter(
    client.POST('/trash/clear'),
    () => undefined
  ),

  reactionToMyPhoto: (photo_id, reaction, add) => responseDataConverter(
    client.POST('/my/{PhotoId}/reaction', {
      params: {
        path: { PhotoId: photo_id }
      },
      body: {
        action: add ? 'add' : 'remove',
        reaction: reaction
      },
    }),
    p => apiPhotoToMyPhoto(p)
  ),

  // GET /trash and GET /trash/index are deprecated (kept server-side for
  // installed mobile builds); the web reads the trash through this cursor API.
  listTrashItems: (cursor, signal) => responseDataConverter(
    client.GET('/trash/items', {
      params: {
        query: { cursor }
      },
      signal
    }),
    apiTrashItemsToTrashPage
  ),

  restorePhotosFromTrash: (photo_ids) => responseDataConverter(
    client.POST('/trash/restore', { body: { photo_ids } }),
    () => undefined
  ),
}